/* eslint-disable no-case-declarations */
/* eslint-disable indent */
import React from 'react';
import {
  UID_DATATYPE,
  TIME_DATATYPE,
  PICTURE_DATATYPE,
  FILE_DATATYPE,
} from 'common/constants/datatypes';
import UIDLink from 'common/components/UIDLink';
import { message } from 'antd';
import { Datatype } from 'common/constants/types';
import { formatAsFullDate, formatAsFullDateAbbrMonth, sortDates } from 'common/utils/time';
import styles from './AttributeTypeInfo.module.scss';
import { AddedAttributeType, AddedAttribute } from '../../../types';
import PreviewPictureAttribute from './components/PreviewPictureAttribute';
import PreviewFileAttribute from './components/PreviewFileAttribute';

export const startDateComparator = (attribute1: AddedAttribute, attribute2: AddedAttribute) => {
  const date1 = attribute1.start_date;
  const date2 = attribute2.start_date;

  // if one of the dates is missing, it should be sorted to the end
  if (!date1) {
    return -1;
  }
  if (!date2) {
    return 1;
  }

  return sortDates(date1, date2);
};

const attributeValueForStartTimesList = (attribute: AddedAttribute, datatype?: Datatype) => {
  switch (datatype) {
    case TIME_DATATYPE:
      return formatAsFullDateAbbrMonth(String(attribute.value));
    case PICTURE_DATATYPE:
      return <PreviewPictureAttribute attributeId={attribute.id} />;
    case FILE_DATATYPE: {
      const { filename } = JSON.parse(attribute.value as string);
      return attribute.id ? <PreviewFileAttribute filename={filename} id={attribute.id} /> : null;
    }
    case UID_DATATYPE:
      return <UIDLink uid={attribute.value as number} />;
    default:
      return attribute.value;
  }
};

const listAttributeValuesAndTimes = (
  attributeType: AddedAttributeType,
  showAttributeStartTime: boolean,
  showAttributeEndTime: boolean
) =>
  attributeType.attributes.sort(startDateComparator).map((attribute: AddedAttribute) => (
    <div key={attributeType.name + attribute.value + attribute.start_date}>
      {attributeValueForStartTimesList(attribute, attributeType.datatype)}
      {showAttributeStartTime && (
        <span className={styles.timeInfo}>
          {' '}
          - from {attribute.start_date ? formatAsFullDate(attribute.start_date) : 'N/A'}
        </span>
      )}
      {showAttributeEndTime && attribute.end_date && (
        <span className={styles.timeInfo}>
          {' '}
          to {formatAsFullDate(attribute.end_date)}
        </span>
      )}
    </div>
  ));

const getAttributeValues = (attributeType: AddedAttributeType) => {
  switch (attributeType.datatype) {
    case UID_DATATYPE:
      return attributeType.attributes.map((attribute, i) => (
        <UIDLink
          comma={attributeType.attributes.length - 1 !== i}
          key={attribute.value as string}
          uid={attribute.value as number}
        />
      ));
    case PICTURE_DATATYPE:
      return attributeType.attributes.map(attribute => (
        <PreviewPictureAttribute attributeId={attribute.id} key={attribute.id} />
      ));
    case FILE_DATATYPE:
      return attributeType.attributes.map(attribute => {
        const { filename } = JSON.parse(attribute.value as string);
        if (attribute.id) {
          return <PreviewFileAttribute filename={filename} id={attribute.id} key={attribute.id} />;
        }
        return message.error(
          `Something went wrong and the file ${filename} is missing an id. Please let someone know there's a bug.`
        );
      });
    default:
      const primaryAttributes: string[] = [];
      const secondaryAttributes: string[] = [];

      attributeType.attributes.forEach(attribute => {
        const currentAttributeValue =
          attributeType.datatype === TIME_DATATYPE
            ? formatAsFullDateAbbrMonth(String(attribute.value))
            : (attribute.value as string);
        if (attribute.is_secondary) {
          secondaryAttributes.push(currentAttributeValue);
        } else {
          primaryAttributes.push(currentAttributeValue);
        }
      });

      if (secondaryAttributes.length === 0) {
        // should not add "primary: " if there are no secondary attributes
        return primaryAttributes.join(', ');
      }
      if (primaryAttributes.length === 0) {
        return `secondary: ${secondaryAttributes.join(', ')}`;
      }

      return (
        <span>
          primary: {primaryAttributes.join(', ')}
          <br />
          secondary: {secondaryAttributes.join(', ')}
        </span>
      );
  }
};

export interface Props {
  attributeType: AddedAttributeType;
  showAttributeStartTime?: boolean;
  showAttributeEndTime?: boolean;
}
const AttributeTypeInfo = ({
  attributeType,
  showAttributeStartTime = false,
  showAttributeEndTime = false,
}: Props) => (
  <div className={styles.info}>
    <div className={styles.description}>{attributeType.description}</div>
    <div className={styles.content}>
      {showAttributeStartTime
        ? listAttributeValuesAndTimes(attributeType, showAttributeStartTime, showAttributeEndTime)
        : getAttributeValues(attributeType)}
    </div>
  </div>
);

export default AttributeTypeInfo;
